import { useState, type ReactNode } from 'react';
import { useAuth } from './auth.js';
import { ApiError, api } from './api.js';
import { PhotoPicker } from './PhotoPicker.js';
import { Button, Card, ErrorNote, Field, Input, Page } from './ui.js';

/**
 * Nome, e-mail, telefone e senha. CPF nunca aparece aqui — é identidade
 * fiscal e não muda por preferência do usuário.
 *
 * Compartilhado pelos dois apps: a única diferença entre passageiro e
 * motorista é o que o chamador passa em `extra` — frota fica de fora
 * daqui e entra como uma seção do motorista.
 */
export function ProfilePage({ extra }: { extra?: ReactNode }) {
  const { user, refresh, logout } = useAuth();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSaved, setProfileSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  if (!user) return null;

  async function saveProfile() {
    setSavingProfile(true);
    setProfileError(null);
    setProfileSaved(false);
    try {
      await api('/api/auth/me', { method: 'PATCH', body: { name, email, phone } });
      await refresh();
      setProfileSaved(true);
    } catch (err) {
      setProfileError(err instanceof ApiError ? err.message : 'não foi possível salvar');
    } finally {
      setSavingProfile(false);
    }
  }

  async function changePassword() {
    setChangingPassword(true);
    setPasswordError(null);
    try {
      await api('/api/auth/me/password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      // Muda a própria sessão junto com as outras — troca de senha
      // desconecta todo mundo, esta aba incluída.
      await logout();
    } catch (err) {
      setPasswordError(err instanceof ApiError ? err.message : 'não foi possível trocar a senha');
      setChangingPassword(false);
    }
  }

  return (
    <Page title="Perfil">
      <Card>
        <PhotoPicker name={user.name} hint="Quem pega a corrida com você reconhece pela foto." />
      </Card>

      <Card className="mt-4 space-y-4">
        <Field label="Nome">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="E-mail">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Telefone">
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>

        {profileError && <ErrorNote>{profileError}</ErrorNote>}
        {profileSaved && !profileError && (
          <p className="text-sm text-good">Dados atualizados.</p>
        )}

        <Button loading={savingProfile} onClick={() => void saveProfile()}>
          Salvar dados
        </Button>
      </Card>

      {extra}

      <Card className="mt-4 space-y-4">
        <p className="font-semibold">Trocar senha</p>
        <Field label="Senha atual">
          <Input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </Field>
        <Field label="Nova senha" hint="Mínimo de 8 caracteres.">
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </Field>

        {passwordError && <ErrorNote>{passwordError}</ErrorNote>}

        <Button
          variant="outline"
          loading={changingPassword}
          disabled={!currentPassword || newPassword.length < 8}
          onClick={() => void changePassword()}
        >
          Trocar senha e sair
        </Button>
      </Card>
    </Page>
  );
}
